import { computed, onBeforeUnmount, onMounted, ref, unref, watch } from '@vue/composition-api';

const resourceCache = new Map();
const resourceSubscribers = new Map();
const DEFAULT_MAX_CACHE_ENTRIES = 250;
const PENDING_ENTRY_MAX_AGE = 60000;

const createCacheEntry = () => ({
    data: null,
    error: null,
    updatedAt: 0,
    promise: null,
    ttl: 0,
    lastAccessedAt: 0
});

const resolveEnabled = (enabled) => {
    if (typeof enabled === 'function') {
        return Boolean(enabled());
    }

    return Boolean(unref(enabled));
};

const resolveResourceKey = (key) => {
    const value = typeof key === 'function' ? key() : unref(key);

    if (Array.isArray(value)) {
        return JSON.stringify(value);
    }

    if (value === null || value === undefined || value === '') {
        return '';
    }

    return String(value);
};

const readCacheEntry = (key) => {
    pruneResourceCache();
    const resolvedKey = resolveResourceKey(key);

    if (!resolvedKey) {
        return null;
    }

    if (!resourceCache.has(resolvedKey)) {
        resourceCache.set(resolvedKey, createCacheEntry());
    }

    const entry = resourceCache.get(resolvedKey);
    entry.lastAccessedAt = Date.now();

    return {
        key: resolvedKey,
        entry
    };
};

const isEntryExpired = (entry, now = Date.now()) => {
    if (!entry || entry.promise) {
        return false;
    }

    if (entry.updatedAt > 0 && entry.ttl > 0) {
        return now - entry.updatedAt > entry.ttl;
    }

    if (entry.updatedAt === 0 && entry.lastAccessedAt > 0) {
        return now - entry.lastAccessedAt > PENDING_ENTRY_MAX_AGE;
    }

    return false;
};

const pruneResourceCache = () => {
    const now = Date.now();

    for (const [key, entry] of resourceCache.entries()) {
        if (isEntryExpired(entry, now)) {
            resourceCache.delete(key);
        }
    }

    if (resourceCache.size <= DEFAULT_MAX_CACHE_ENTRIES) {
        return;
    }

    const removableEntries = [...resourceCache.entries()].filter(([, entry]) => !entry.promise).sort((left, right) => (left[1].lastAccessedAt || 0) - (right[1].lastAccessedAt || 0));

    const overflow = resourceCache.size - DEFAULT_MAX_CACHE_ENTRIES;

    removableEntries.slice(0, overflow).forEach(([key]) => {
        resourceCache.delete(key);
    });
};

export async function loadCachedResource({ key, loader, ttl = 30000, force = false }) {
    const cacheEntry = readCacheEntry(key);

    if (!cacheEntry) {
        return null;
    }

    const { entry } = cacheEntry;
    entry.ttl = Math.max(0, Number(ttl) || 0);
    const isFresh = entry.updatedAt > 0 && Date.now() - entry.updatedAt < ttl;

    if (!force && isFresh) {
        return entry.data;
    }

    if (entry.promise && !force) {
        return entry.promise;
    }

    entry.promise = Promise.resolve(loader())
        .then((data) => {
            entry.data = data;
            entry.error = null;
            entry.updatedAt = Date.now();
            entry.lastAccessedAt = Date.now();
            pruneResourceCache();

            return data;
        })
        .catch((error) => {
            entry.error = error;
            entry.lastAccessedAt = Date.now();
            throw error;
        })
        .finally(() => {
            entry.promise = null;
        });

    return entry.promise;
}

export function invalidateCachedResource(key) {
    const resolvedKey = resolveResourceKey(key);

    if (!resolvedKey) {
        return Promise.resolve([]);
    }

    resourceCache.delete(resolvedKey);

    const subscribers = resourceSubscribers.get(resolvedKey);

    if (!subscribers?.size) {
        return Promise.resolve([]);
    }

    return Promise.allSettled(
        [...subscribers].map((callback) => {
            try {
                return callback();
            } catch (error) {
                return Promise.reject(error);
            }
        })
    );
}

export function clearRemoteResourceCache() {
    resourceCache.clear();
    resourceSubscribers.clear();
}

export function useRemoteResource({ key, loader, immediate = true, ttl = 30000, enabled = true }) {
    const data = ref(null);
    const error = ref(null);
    const status = ref(immediate && resolveEnabled(enabled) && Boolean(resolveResourceKey(key)) ? 'loading' : 'idle');
    const lastLoadedAt = ref(0);
    let requestVersion = 0;
    let unsubscribe = null;

    const subscribeToInvalidation = (resolvedKey) => {
        if (!resolvedKey) {
            return () => {};
        }

        if (!resourceSubscribers.has(resolvedKey)) {
            resourceSubscribers.set(resolvedKey, new Set());
        }

        const subscribers = resourceSubscribers.get(resolvedKey);
        const callback = () => {
            if (!resolveEnabled(enabled) || resolveResourceKey(key) !== resolvedKey) {
                return Promise.resolve(null);
            }

            return load({ force: true }).catch(() => null);
        };

        subscribers.add(callback);

        return () => {
            subscribers.delete(callback);

            if (!subscribers.size) {
                resourceSubscribers.delete(resolvedKey);
            }
        };
    };

    const syncSubscription = () => {
        unsubscribe?.();
        unsubscribe = subscribeToInvalidation(resolveResourceKey(key));
    };

    const load = async ({ force = false } = {}) => {
        const isEnabled = resolveEnabled(enabled);
        const resolvedKey = resolveResourceKey(key);
        const currentRequestVersion = ++requestVersion;

        if (!isEnabled || !resolvedKey) {
            status.value = 'idle';
            data.value = null;
            error.value = null;
            return null;
        }

        status.value = data.value && !force ? 'refreshing' : 'loading';
        error.value = null;

        try {
            const result = await loadCachedResource({
                key: resolvedKey,
                loader,
                ttl,
                force
            });

            if (currentRequestVersion !== requestVersion || resolveResourceKey(key) !== resolvedKey || !resolveEnabled(enabled)) {
                return result;
            }

            data.value = result;
            status.value = 'success';
            lastLoadedAt.value = Date.now();

            return result;
        } catch (caughtError) {
            if (currentRequestVersion !== requestVersion || resolveResourceKey(key) !== resolvedKey || !resolveEnabled(enabled)) {
                return null;
            }

            error.value = caughtError;
            status.value = 'error';
            throw caughtError;
        }
    };

    watch(
        () => [resolveResourceKey(key), resolveEnabled(enabled)],
        ([nextKey, isEnabled], [previousKey, wasEnabled]) => {
            if (nextKey !== previousKey) {
                syncSubscription();
            }

            if (!immediate) {
                return;
            }

            if (!isEnabled) {
                requestVersion += 1;
                status.value = 'idle';
                data.value = null;
                error.value = null;
                return;
            }

            if (nextKey !== previousKey || isEnabled !== wasEnabled) {
                load().catch(() => {});
            }
        }
    );

    onMounted(() => {
        syncSubscription();

        if (immediate) {
            load().catch(() => {});
        }
    });

    onBeforeUnmount(() => {
        requestVersion += 1;
        unsubscribe?.();
    });

    return {
        data,
        error,
        status,
        lastLoadedAt,
        isLoading: computed(() => status.value === 'loading' || status.value === 'refreshing'),
        isRefreshing: computed(() => status.value === 'refreshing'),
        isReady: computed(() => status.value === 'success'),
        isEmpty: computed(() => {
            if (status.value !== 'success') {
                return false;
            }

            if (data.value == null) {
                return true;
            }

            if (Array.isArray(data.value)) {
                return data.value.length === 0;
            }

            if (Array.isArray(data.value.items)) {
                return data.value.items.length === 0;
            }

            return false;
        }),
        load,
        refresh: () => load({ force: true })
    };
}
